import uuidv4 from 'uuid/v4';
import _ from 'lodash';
import basic from './common/basic.js';
import updater from '../updater.js';

/* State example:
state: {
  "byId": {
    "var_0": {
      "id": "var_0",
      "name": "Strength",
      "type": "Integer",
      "value": "0"
    },
    "var_1": {
      "id": "var_1",
      "name": "Has_key",
      "type": "Boolean",
      "value": "false"
    },
  }
}
*/

const NEW_VAR = {
  type: 'string',
  value: '',
};

const state = { byId: {} };

const getters = {
  ...basic.getters({}),

  /**
   * Returns the variables in an associative array
   * @returns {Object}
   */
  getVariables(state) {
    return state.byId;
  },

  /**
   * Returns the variable values to be used in application variables
   * @returns {Object}  The values with key the variable name and value the default variable value
   */
  getDefaultValues(state) {
    return Object.fromEntries(
      Object.entries(state.byId)
        .filter(([k, v]) => !v.children)
        .map(([k, v]) => [k, v.value])
    );
  },

  /**
   * Returns the variable object when searched by variable name
   * @param   {string}  name  The name of the variable to search
   * @returns {Object}        The variable object or undefined if such a variable does not exist
   */
  getByName(state) {
    return name => {
      return Object.values(state.byId).find(v => v.name === name);
    };
  },

  /**
   * Returns the (non-null) variable names in an array
   */
  getNames(state) {
    return Object.values(state.byId)
      .filter(v => v.name && !v.children)
      .map(v => v.name);
  },
};

const actions = {
  ...basic.actions({
    type: 'variables',
    container: { type: 'variables', prop: 'children' },
  }),

  /**
   * Create a variable
   * @param {Object}  props       Properties overriding the default ones
   * @param {Number}  tIndex      The index in the root container
   * @param {Boolean} apply       Whether to apply the patch after finishing
   */
  customCreate(context, { props, tIndex, apply }) {
    const id = uuidv4();
    const node = { id, ...NEW_VAR, ...props };

    // All variables are added to root
    const tId = context.getters.getRootContainer.id;
    context.dispatch('create', { node, tId, tIndex, apply: false });
    updater.complete({ apply });
    return Promise.resolve(id);
  },

  /**
   * Update some of the variable's properties
   * @param {String}  id      The attribute id
   * @param {Object}  props   The properties that are new or changed
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  customUpdate(context, { id, props, apply }) {
    context.dispatch('update', { id, props, apply: false });
    updater.complete({ apply });
  },

  /**
   * Delete a variable
   * @param {String}  id      The variable id
   * @param {Booelan} apply   Whether to apply the patch after finishing
   */
  customDelete(context, { id, apply }) {
    context.dispatch('delete', { id, apply: false });
    updater.complete({ apply });
  },
};

const mutations = {};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default module;
